// Zvonalitik Dashboard Store Module
// Dashboard for viewing call analytics projects with summary metrics
import axiosConfig from '@/lib/axiosConfig';

const state = {
  // Projects (collections) list with metrics
  projects: [],

  // Global summary stats
  globalStats: {
    total_projects: 0,
    total_calls: 0,
    avg_score: 0,
    total_duration_minutes: 0,
    exemplary_count: 0,
    failed_count: 0
  },

  // Filters
  filters: {
    period: 'all',        // all | today | last_7_days | last_30_days
    sortBy: 'date_desc',  // date_desc | date_asc | score_desc | score_asc | calls_desc
    search: ''
  },

  // Loading states
  loading: false,
  error: null
};

const getters = {
  getProjects: (state) => state.projects,
  getGlobalStats: (state) => state.globalStats,
  getFilters: (state) => state.filters,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,

  // Filter and sort projects locally
  filteredProjects: (state) => {
    let filtered = [...state.projects];

    // Apply search filter locally
    if (state.filters.search && state.filters.search.trim()) {
      const searchLower = state.filters.search.toLowerCase().trim();
      filtered = filtered.filter(project => {
        const caption = (project.caption || '').toLowerCase();
        const description = (project.description || '').toLowerCase();
        return caption.includes(searchLower) || description.includes(searchLower);
      });
    }

    // Apply local sorting
    switch (state.filters.sortBy) {
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at));
        break;
      case 'score_desc':
        filtered.sort((a, b) => (b.summary?.avg_score || 0) - (a.summary?.avg_score || 0));
        break;
      case 'score_asc':
        filtered.sort((a, b) => (a.summary?.avg_score || 0) - (b.summary?.avg_score || 0));
        break;
      case 'calls_desc':
        filtered.sort((a, b) => (b.summary?.calls_count || 0) - (a.summary?.calls_count || 0));
        break;
      case 'date_desc':
      default:
        filtered.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        break;
    }

    return filtered;
  },

  // Total projects count
  totalProjectsCount: (state) => state.projects.length
};

const mutations = {
  SET_PROJECTS(state, projects) {
    state.projects = projects;
  },

  SET_GLOBAL_STATS(state, stats) {
    state.globalStats = {
      total_projects: stats.total_projects || 0,
      total_calls: stats.total_calls || 0,
      avg_score: stats.avg_score || 0,
      total_duration_minutes: stats.total_duration_minutes || 0,
      exemplary_count: stats.exemplary_count || 0,
      failed_count: stats.failed_count || 0
    };
  },

  SET_FILTER(state, { filterName, value }) {
    state.filters[filterName] = value;
  },

  RESET_FILTERS(state) {
    state.filters = {
      period: 'all',
      sortBy: 'date_desc',
      search: ''
    };
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  // Update single project in list
  UPDATE_PROJECT(state, updatedProject) {
    const index = state.projects.findIndex(p => p.id === updatedProject.id);
    if (index !== -1) {
      state.projects.splice(index, 1, { ...state.projects[index], ...updatedProject });
    }
  }
};

const actions = {
  // Fetch dashboard with projects list and stats
  async fetchDashboard({ commit, state, rootState }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const params = {
        action: 'get_zvonalitik_dashboard',
        period: state.filters.period
      };

      // Add chatbot_name for JWT auth
      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      const response = await axiosConfig.get('', { params });

      if (response.data) {
        // Set projects
        if (response.data.projects) {
          commit('SET_PROJECTS', response.data.projects);
        }

        // Set global stats
        if (response.data.globalStats) {
          commit('SET_GLOBAL_STATS', response.data.globalStats);
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching zvonalitik dashboard:', error);
      commit('SET_ERROR', error.message || 'Failed to load dashboard');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Set filter and reload data
  async setFilter({ commit, dispatch }, { filterName, value }) {
    commit('SET_FILTER', { filterName, value });

    // Search and sort are applied locally, no need to refetch
    if (filterName === 'search' || filterName === 'sortBy') {
      return;
    }

    // For period filter, refetch from server
    await dispatch('fetchDashboard');
  },

  // Reset all filters to defaults
  resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS');
    return dispatch('fetchDashboard');
  },

  // Clear dashboard state
  clearDashboard({ commit }) {
    commit('SET_PROJECTS', []);
    commit('SET_GLOBAL_STATS', {
      total_projects: 0,
      total_calls: 0,
      avg_score: 0,
      total_duration_minutes: 0,
      exemplary_count: 0,
      failed_count: 0
    });
    commit('RESET_FILTERS');
    commit('SET_ERROR', null);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
